import ConfirmDeleteModal from "./ConfirmDeleteModal";
import SeedTypeManagementContext from "../../../contexts/seed-type-management/SeedTypeManagementContext";
import AuthContext from "../../../contexts/auth/AuthContext";
import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import InputField from "../../layout/InputField";
import LabeledInputField from "../../layout/LabeledInputField";
import useFarmerAppPolicy from "../../../customHooks/useFarmerAppPolicy";
import { TENANT_ADMIN } from "../../../constants/roles/roles";
import Button from "../../layout/Button";
import Loading from "../../ui-elements/Loading";

const Card = ({ seedType }) => {
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [showConfirmUpdateModal, setShowConfirmUpdateModal] = useState(false); // NEW
  const [editEnabled, setEditEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const { updateSeedType, deleteSeedType } = useContext(
    SeedTypeManagementContext
  );
  const {
    currentTenant,
    currentContainer,
    loadAndSetAccessibleSeedTypesAsync,
  } = useContext(AuthContext);
  const tenantAdminPolicy = useFarmerAppPolicy([TENANT_ADMIN]);
  const navigate = useNavigate();
  const INT32_MAX = 2147483647;
  const INT32_MAX_BIG = 2147483647n;
  
  const initialState = {
    name: {
      value: seedType.name ?? "",
      touched: false,
      validate: (text) => text.length >= 3,
      isValid: !!seedType?.name,
      placeholder: "Seed Type name ...",
    },
    scientificName: {
      value: seedType.scientificName ?? "",
      touched: false,
      validate: (text) => text.length >= 3,
      isValid: !!seedType?.scientificName,
      placeholder: "Scientific name ...",
    },
    commonName: {
      value: seedType.commonName ?? "",
      touched: false,
      validate: (text) => text.length >= 3,
      isValid: !!seedType?.commonName,
    },
    seedsPerPlug: {
      value: seedType.seedsPerPlug ?? "",
      touched: false,
      validate: (str) => parseInt(str, 10) >= 1,
      isValid: !!seedType?.seedsPerPlug,
    },
  };

  const [seedTypeState, setSeedTypeState] = useState(initialState);

  // When the seedType prop changes, update the state
  useEffect(() => {
    if (!seedType) return;
    const next = Object.entries(seedType).reduce((acc, [key, value]) => {
      if (!(key in seedTypeState)) return acc;
      return {
        ...acc,
        [key]: { ...acc[key], value, isValid: acc[key]?.validate(value) },
      };
    }, initialState);
    setSeedTypeState(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedType]);

  // Handle input changes
  const onChange = (e) => {
    e.persist();
    const key = e.currentTarget.name;
    let value = e.currentTarget.value;
    if (key === 'seedsPerPlug') {
      value = clampInt32String(value);
    }
    setSeedTypeState((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        value,
        isValid: prev[key].validate(value),
        touched: true,
      },
    }));
  };

  // Create the request body for the update
  const createRequestBody = () => {
    const props = Object.entries(seedTypeState).reduce(
      (acc, [key, vo]) => ({ ...acc, [key]: vo.value }),
      {}
    );
    return { ...seedType, ...props };
  };

  // Handle save action
  const handleSave = () => {
    if (!isStateValid || !isStateTouched) return;
    setShowConfirmUpdateModal(true);
  };

  // Confirm update action
  const confirmUpdate = async () => {
    setLoading(true);
    try {
      await updateSeedType(createRequestBody());
      await loadAndSetAccessibleSeedTypesAsync(
        currentTenant,
        currentContainer,
        true,
        "seedVariantTypes"
      );
      setEditEnabled(false);
    } finally {
      setLoading(false);
      setShowConfirmUpdateModal(false);
    }
  };

  const clampInt32String = (raw) => {
    if (raw === '') 
    {
      return raw;
    }
    if (!/^\d+$/.test(raw)) 
    {
      return raw;
    }
    return BigInt(raw) > INT32_MAX_BIG ? String(INT32_MAX) : raw;
  };
  const isStateValid = Object.values(seedTypeState).every((e) => !!e.isValid);
  const isStateTouched = Object.values(seedTypeState).some((e) => !!e.touched);

  // Handle delete action
  const handleDelete = () => setShowConfirmDeleteModal(true);
  const onCancelConfirmEdit = () => setShowConfirmDeleteModal(false);
  const confirmDelete = async () => {
    setLoading(true);
    try {
      await deleteSeedType(seedType);
      await loadAndSetAccessibleSeedTypesAsync(
        currentTenant,
        currentContainer,
        false,
        "seedVariantTypes"
      );
    } finally {
      setLoading(false);
      setShowConfirmDeleteModal(false);
    }
  };

  // Navigate to seed variant types
  const handleVariants = () => {
    navigate(`/management/seed-types/${seedType.id}/seed-variant-types/list`);
  };

  const toggleEdit = () => {
    setEditEnabled(!editEnabled);
    setSeedTypeState(initialState);
  };

  return loading ? (
    <Loading fullScreen={true} />
  ) : (
    <>
      <ConfirmDeleteModal
        show={showConfirmDeleteModal}
        onCancel={onCancelConfirmEdit}
        onConfirm={confirmDelete}
        header="DO YOU REALLY WANT TO DELETE?"
        message={
          "Deleting this seed type will permanently remove it from the available choices for seeding. Furthermore, all associated seed type variants will also be deleted. This action is irreversible."
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      <ConfirmDeleteModal
        show={showConfirmUpdateModal}
        onCancel={() => setShowConfirmUpdateModal(false)}
        onConfirm={confirmUpdate}
        header="ALTER SEED TYPE"
        message={
          "Changing seed type properties will alter the data of every plant ever created with this seed type. Do you want to save these changes?"
        }
        confirmLabel="Save"
        cancelLabel="Cancel"
      />

      <div className="seed-type-card">
        <div className="seed-type-card-header">
          <div className="seed-type-name-settings">
            <InputField
              inputName={"name"}
              value={seedTypeState.name.value}
              isValid={seedTypeState.name.isValid}
              touched={seedTypeState.name.touched}
              inputType={"text"}
              onChange={onChange}
              placeholder={"Name"}
              disabled={editEnabled === false}
              errorMessage={"Minimum 3 characters."}
            />
          </div>

          <div className="seed-type-card-number">N° {seedType.number}</div>

          <div className="seed-type-scientificName-settings">
            <InputField
              inputName={"scientificName"}
              value={seedTypeState.scientificName.value}
              isValid={seedTypeState.scientificName.isValid}
              touched={seedTypeState.scientificName.touched}
              inputType={"text"}
              onChange={onChange}
              placeholder={"Scientific Name"}
              disabled={editEnabled === false}
              errorMessage={"Minimum 3 characters."}
            />
          </div>
        </div>

        <div className="seed-type-commonName-settings">
          <LabeledInputField
            labelText={"Common Name"}
            inputName={"commonName"}
            value={seedTypeState.commonName.value}
            isValid={seedTypeState.commonName.isValid}
            touched={seedTypeState.commonName.touched}
            inputType={"text"}
            onChange={onChange}
            placeholder={"Common name"}
            disabled={editEnabled === false}
            errorMessage={"Minimum 3 characters."}
          />
        </div>

        <div className="seed-type-seedsPerPlug-settings">
          <LabeledInputField
            labelText={"Seeds per Plug"}
            inputName={"seedsPerPlug"}
            value={seedTypeState.seedsPerPlug.value}
            isValid={seedTypeState.seedsPerPlug.isValid}
            touched={seedTypeState.seedsPerPlug.touched}
            inputType={"number"}
            onChange={onChange}
            placeholder={"Seeds per Plug"}
            max={INT32_MAX}
            disabled={editEnabled === false}
            errorMessage={"Has to be 1 or higher."}
          />
        </div>

        {!editEnabled ? (
          <>
            <Button
              className="seed-type-card-delete seed-type-card-button"
              label="Delete"
              clickHandler={handleDelete}
              disabled={tenantAdminPolicy.isForbidden()}
            />
            <Button
              className="seed-type-card-variants seed-type-card-button"
              label="Variants"
              clickHandler={handleVariants}
            />
            <Button
              className="seed-type-card-edit seed-type-card-button"
              label="Edit"
              clickHandler={toggleEdit}
              disabled={tenantAdminPolicy.isForbidden()}
            />
          </>
        ) : (
          <>
            <div
              className="seed-type-card-cancel seed-type-card-button"
              onClick={toggleEdit}
            >
              Cancel
            </div>
            <div
              className={
                (!isStateValid || !isStateTouched ? "disabled " : "") +
                "seed-type-card-save seed-type-card-button"
              }
              onClick={handleSave}
            >
              Save
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Card;
